<template>
  <StepShell
    :title="t('completeV2.steps.userInfos.screenTitle')"
    :intro="t('completeV2.steps.userInfos.screenIntro')"
    :can-go-back="canGoBack"
    :continue-disabled="v$.$invalid"
    :continue-loading="saving"
    @next="handleNext"
    @back="$emit('back')"
  >
    <div class="user-infos">
      <section class="user-infos__section">
        <header class="user-infos__section-header">
          <span class="user-infos__section-icon">
            <i class="far fa-user" />
          </span>
          <h3 class="user-infos__section-title">
            {{ t('completeV2.steps.userInfos.sections.contact') }}
          </h3>
        </header>

        <div class="user-infos__grid">
          <div class="user-infos__field">
            <label
              for="ui-firstname"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.firstName') }}</label>
            <InputText
              id="ui-firstname"
              v-model="form.firstName"
              :class="{ 'p-invalid': v$.firstName.$dirty && v$.firstName.$invalid }"
              @blur="v$.firstName.$touch()"
            />
          </div>
          <div class="user-infos__field">
            <label
              for="ui-lastname"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.lastName') }}</label>
            <InputText
              id="ui-lastname"
              v-model="form.lastName"
              :class="{ 'p-invalid': v$.lastName.$dirty && v$.lastName.$invalid }"
              @blur="v$.lastName.$touch()"
            />
          </div>
          <div class="user-infos__field user-infos__field--full">
            <label
              for="ui-phone"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.phoneMobile') }}</label>
            <InputText
              id="ui-phone"
              v-model="form.phoneMobile"
              :class="{ 'p-invalid': v$.phoneMobile.$dirty && v$.phoneMobile.$invalid }"
              @blur="v$.phoneMobile.$touch()"
            />
          </div>
        </div>
      </section>

      <section class="user-infos__section">
        <header class="user-infos__section-header">
          <span class="user-infos__section-icon">
            <i class="far fa-building" />
          </span>
          <h3 class="user-infos__section-title">
            {{ t('completeV2.steps.userInfos.sections.company') }}
          </h3>
        </header>

        <div class="user-infos__grid">
          <div class="user-infos__field">
            <label
              for="ui-societe"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.societe') }}</label>
            <InputText
              id="ui-societe"
              v-model="form.societe"
              :class="{ 'p-invalid': v$.societe.$dirty && v$.societe.$invalid }"
              @blur="v$.societe.$touch()"
            />
          </div>
          <div class="user-infos__field">
            <label
              for="ui-vat"
              class="user-infos__label"
            >
              {{ t('profile.myInformation.fields.vatNumber') }}
              <span class="user-infos__optional-tag">{{ t('completeV2.optional') }}</span>
            </label>
            <InputText
              id="ui-vat"
              v-model="form.vatNumber"
            />
          </div>
        </div>
      </section>

      <section class="user-infos__section">
        <header class="user-infos__section-header">
          <span class="user-infos__section-icon">
            <i class="far fa-location-dot" />
          </span>
          <h3 class="user-infos__section-title">
            {{ t('completeV2.steps.userInfos.sections.address') }}
          </h3>
        </header>

        <div class="user-infos__grid user-infos__grid--address">
          <div class="user-infos__field">
            <label
              for="ui-rue"
              class="user-infos__label"
            >{{ t('complete.userInfos.fields.address') }}</label>
            <InputText
              id="ui-rue"
              v-model="form.rue"
              :class="{ 'p-invalid': v$.rue.$dirty && v$.rue.$invalid }"
              @blur="v$.rue.$touch()"
            />
          </div>
          <div class="user-infos__field">
            <label
              for="ui-zip"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.zipCode') }}</label>
            <InputText
              id="ui-zip"
              v-model="form.zipCode"
              :class="{ 'p-invalid': v$.zipCode.$dirty && v$.zipCode.$invalid }"
              @blur="v$.zipCode.$touch()"
            />
          </div>
        </div>

        <div class="user-infos__grid">
          <div class="user-infos__field">
            <label
              for="ui-city"
              class="user-infos__label"
            >{{ t('profile.myInformation.fields.city') }}</label>
            <InputText
              id="ui-city"
              v-model="form.city"
              :class="{ 'p-invalid': v$.city.$dirty && v$.city.$invalid }"
              @blur="v$.city.$touch()"
            />
          </div>
          <div class="user-infos__field">
            <label class="user-infos__label">
              {{ t('countries.label') }}
            </label>
            <CountryDropdown
              v-if="hydrated"
              v-model="countryWrapper"
              :configuration="countryConfig"
              :styles="countryStyles"
            />
          </div>
        </div>
      </section>
    </div>
  </StepShell>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import useVuelidate from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import { useOnboardingProfile } from '@/composables/onboarding-v2/useOnboardingProfile';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'UserInfosStep',

  components: {
    StepShell,
    InputText,
    CountryDropdown,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const {
      profile, profileRaw, preload, saveProfile,
    } = useOnboardingProfile();

    const form = reactive({
      firstName: '',
      lastName: '',
      phoneMobile: '',
      societe: '',
      vatNumber: '',
      rue: '',
      zipCode: '',
      city: '',
      country: '',
    });

    const countryWrapper = computed({
      get: () => ({ country: form.country, state: '' }),
      set: (value: { country: string; state: string }) => {
        form.country = value.country;
      },
    });

    const countryConfig = {
      showLabels: false,
      showStates: false,
      showFlags: true,
      showPhonesCodes: false,
      showOnlyPhoneCodeForSelection: false,
    };

    const countryStyles = {
      component: 'width:100%',
      country: 'width:100%; align-items: center; height: 2.3rem;',
      state: 'width:100%',
    };

    const hydrated = ref(false);
    let initialSnapshot = '';

    const snapshot = () => JSON.stringify({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phoneMobile: form.phoneMobile.trim(),
      societe: form.societe.trim(),
      vatNumber: form.vatNumber.trim(),
      rue: form.rue.trim(),
      zipCode: form.zipCode.trim(),
      city: form.city.trim(),
      country: form.country,
    });

    const hydrate = () => {
      form.firstName = profile.firstName;
      form.lastName = profile.lastName;
      form.phoneMobile = profile.phoneMobile;
      form.societe = profile.societe;
      form.vatNumber = profile.vatNumber;
      form.rue = profile.rue;
      form.zipCode = profile.zipCode;
      form.city = profile.city;
      form.country = profile.country;
      initialSnapshot = snapshot();
    };

    onMounted(async () => {
      await preload();
      hydrate();
      hydrated.value = true;
    });

    const rules = computed(() => ({
      firstName: { required, minLength: minLength(2) },
      lastName: { required, minLength: minLength(2) },
      phoneMobile: { required },
      societe: { required, minLength: minLength(1) },
      rue: { required, minLength: minLength(2) },
      zipCode: { required },
      city: { required, minLength: minLength(2) },
      country: { required },
    }));

    const v$ = useVuelidate(rules, form);

    const saving = ref(false);

    const handleNext = async () => {
      const ok = await v$.value.$validate();

      if (!ok) return;
      if (snapshot() === initialSnapshot) {
        emit('next');
        return;
      }
      saving.value = true;
      try {
        profileRaw.firstName = form.firstName.trim();
        profileRaw.lastName = form.lastName.trim();
        profileRaw.phoneMobile = form.phoneMobile.trim();
        profileRaw.societe = form.societe.trim();
        profileRaw.vatNumber = form.vatNumber.trim();
        profileRaw.rue = form.rue.trim();
        profileRaw.zipCode = form.zipCode.trim();
        profileRaw.city = form.city.trim();
        profileRaw.country = form.country;
        await saveProfile();
        initialSnapshot = snapshot();
        emit('next');
      } catch (err) {
        await showToastError('GENERIC_ERROR');
      } finally {
        saving.value = false;
      }
    };

    return {
      t,
      form,
      v$,
      saving,
      hydrated,
      countryWrapper,
      countryConfig,
      countryStyles,
      handleNext,
    };
  },
});
</script>

<style lang="scss" scoped>
.user-infos {
  display: flex;
  flex-direction: column;
  gap: 1.85rem;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.95rem;
  }

  &__section-header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.1rem;
  }

  &__section-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.14);
    color: #5d8a24;
    font-size: 0.82rem;
  }

  &__section-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: 0.01em;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;

    &--address {
      grid-template-columns: 2fr 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__optional-tag {
    font-size: 0.65rem;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
  }
}

:deep(.p-inputtext) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover { border-color: #94c840; }
  &:focus,
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.CountryDropdown .p-dropdown) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  align-items: center;

  &:hover { border-color: #94c840; }
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

@media (max-width: 640px) {
  .user-infos__grid {
    grid-template-columns: 1fr;
  }
}
</style>
